import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  onClose: (id: string) => void;
  className?: string;
}

const toastIcons = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

// These land AFTER the toast's own `bg-white dark:bg-gray-900` and go through
// twMerge, so whatever background they name REPLACES it rather than tinting it.
// That is why the dark variants must be opaque (-950) and not an alpha tint: a
// `dark:bg-green-900/20` here left the toast 80% see-through, with the page
// scrolling behind the text. Light mode was never affected because -50 is
// opaque. Keep both sides opaque.
const toastStyles = {
  success: 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
  error: 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300',
  warning: 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300',
  info: 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
};

export function Toast({ id, type, title, message, duration, onClose, className }: ToastProps) {
  const Icon = toastIcons[type];
  const [isClosing, setIsClosing] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Respect prefers-reduced-motion
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    } else if (typeof media.addListener === 'function') {
      media.addListener(update);
      return () => media.removeListener(update);
    }
  }, []);

  // Auto-dismiss functionality
  useEffect(() => {
    if (duration && duration > 0 && !isClosing) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, isClosing]);

  // Trigger enter transition after mount without relying on Tailwind animate utilities
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    // Delay the actual close to allow animation to complete (keep in sync with duration below)
    setTimeout(() => onClose(id), 320);
  };

  const isEntering = mounted && !isClosing;

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg shadow-lg border max-w-md',
        'bg-white dark:bg-gray-900 text-foreground',
        'border-gray-200 dark:border-gray-800',
        'transition-all duration-300',
        !mounted && 'opacity-0 -translate-y-2 scale-95',
        isEntering && 'opacity-100 translate-y-0 scale-100',
        isClosing && 'opacity-0 translate-x-2 scale-95',
        toastStyles[type],
        className
      )}
      style={{
        willChange: 'transform, opacity',
        transitionTimingFunction: isClosing
          ? 'cubic-bezier(0.4, 0, 0.2, 1)'
          : 'cubic-bezier(0.16, 1, 0.3, 1)',
        transitionDuration: reducedMotion ? '0ms' : undefined,
      }}
    >
      <Icon className="h-5 w-5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        {title && (
          <p className="text-sm font-semibold mb-1">{title}</p>
        )}
        <p className="text-sm">{message}</p>
      </div>
      <button
        onClick={handleClose}
        className="p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors flex-shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export interface ToastContainerProps {
  toasts: ToastProps[];
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  className?: string;
}

const positionStyles = {
  'top-right': 'fixed top-4 right-4 z-[100001]',
  'top-left': 'fixed top-4 left-4 z-[100001]',
  'bottom-right': 'fixed bottom-4 right-4 z-[100001]',
  'bottom-left': 'fixed bottom-4 left-4 z-[100001]',
  'top-center': 'fixed top-4 left-1/2 transform -translate-x-1/2 z-[100001]',
  'bottom-center': 'fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[100001]',
};

export function ToastContainer({ toasts, position = 'top-right', className }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className={cn(positionStyles[position], className)}>
      <div className="space-y-3">
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            className="transition-all duration-200 ease-out"
            style={{
              zIndex: 100001 - index,
              // Subtle stacking offset for multiple toasts
              transform: index > 0 ? `translateX(${index * 4}px) scale(${1 - index * 0.02})` : 'none',
              opacity: index > 2 ? 0.8 : 1,
            }}
          >
            <Toast {...toast} />
          </div>
        ))}
      </div>
    </div>
  );
}